import * as React from 'react';

import { AsyncData } from '../../redux/core';
import {
  DetailsList,
  Spinner,
  SpinnerSize,
  IPersonaProps,
  IColumn,
  Persona,
  PersonaSize,
  IconButton,
  Panel,
  PanelType,
  SearchBox,
  CommandBarButton,
  MessageBar,
  MessageBarType,
  CheckboxVisibility,
  IBasePickerSuggestionsProps,
  NormalPeoplePicker,
} from '@fluentui/react';
import { ProviderData } from '../../api/providers.types';
import { connect } from 'react-redux';

import { Dispatch, Action } from 'redux';
import { HealAPI } from '../../healApi';
import { RootState } from '../../redux/types';
import { ToastParams } from '../../redux/message/types';
import { bindActionCreators } from 'redux';
import { setShowPairingRules } from '../../redux/ui/actions';
import { toast } from '../../redux/message/actions';
import { OkCancelDialog } from './OkCancelDialog';
import { NotAllowedMatch } from '../../redux/scheduling/types';
import { fetchPairingRules } from '../../redux/scheduling/actions';

interface OwnProps {}

interface StateProps {
  showPairingRules: boolean;
  allProviders: AsyncData<ProviderData[]>;
  pairingRules: AsyncData<NotAllowedMatch[]>;
}

interface DispatchProps {
  actions: {
    toast: (params: ToastParams) => void;
    setShowPairingRules: (show: boolean) => void;
    fetchPairingRules: () => void;
  };
}

const suggestionProps: IBasePickerSuggestionsProps = {
  noResultsFoundText: 'No results found!',
};

export type PairingRulesSidePanelProps = StateProps & DispatchProps;

export interface PairingRulesSidePanelState {
  columns: IColumn[];
  insertingNewPairingRule: boolean;
  pairingRulesItemsList: NotAllowedMatch[];
  providerIdToProvider: { [key: string]: ProviderData };
  selectedDoctorId?: string;
  selectedAssistantId?: string;
  filterText?: string;
}

class PairingRules extends React.Component<PairingRulesSidePanelProps, PairingRulesSidePanelState> {
  constructor(props: any) {
    super(props);
    this.state = {
      columns: this.buildColumns(),
      insertingNewPairingRule: false,
      providerIdToProvider: {},
      pairingRulesItemsList: [],
    };
  }

  public componentDidUpdate(prevProps: PairingRulesSidePanelProps) {
    if (this.props.allProviders !== prevProps.allProviders) {
      this.generateProviderConversions(this.props);
    }
    if (this.props.allProviders.data && this.props.pairingRules.data && this.props.pairingRules.data !== prevProps.pairingRules.data) {
      this.setPairingRules(this.props.pairingRules.data || []);
    }
  }

  public componentDidMount() {
    if (this.props.allProviders.data) {
      this.generateProviderConversions(this.props);
    }
  }

  public render(): JSX.Element {
    return (
      <Panel
        isOpen={this.props.showPairingRules}
        onDismiss={this.hidePairingRulesSidePanel}
        type={PanelType.medium}
        headerText="Manage not-allowed matches"
        closeButtonAriaLabel="Close"
      >
        <div className="flex">
          <div style={{ marginRight: 10, width: 320 }}>
            <SearchBox placeholder="Filter providers" onChange={this.onSearchChange} />
          </div>
          <CommandBarButton
            className="flex-self-end"
            style={{ height: 32, marginRight: 10 }}
            iconProps={{ iconName: 'Add' }}
            onClick={this.onAddClicked}
            text="Add"
            disabled={this.state.insertingNewPairingRule}
          />
        </div>
        {this.renderViewPairingRules()}
      </Panel>
    );
  }

  private renderViewPairingRules(): JSX.Element {
    if (!this.props.allProviders.data || this.props.allProviders.isFetching) {
      return <Spinner style={{ marginTop: 60 }} size={SpinnerSize.large} label="Loading pairing rules..." />;
    }

    let items = this.state.pairingRulesItemsList;
    let { filterText } = this.state;
    if (filterText) {
      filterText = filterText.toLowerCase();
      items = this.state.pairingRulesItemsList.filter(
        (i) =>
          i.new ||
          this.state.providerIdToProvider[i.providerOneId].fullName.toLowerCase().indexOf(filterText!) > -1 ||
          this.state.providerIdToProvider[i.providerTwoId].fullName.toLowerCase().indexOf(filterText!) > -1
      );
    }

    return (
      <>
        <DetailsList compact={true} checkboxVisibility={CheckboxVisibility.hidden} items={items} columns={this.state.columns} />
        {(items || []).length === 0 && (
          <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
            No pairing rules found!
          </MessageBar>
        )}
      </>
    );
  }

  private renderNormalPicker(providerType: string) {
    return (
      <NormalPeoplePicker
        onResolveSuggestions={providerType === 'DOCTOR' ? this.onSearchChangeDoctor : this.onSearchChangeMA}
        pickerSuggestionsProps={suggestionProps}
        onChange={providerType === 'DOCTOR' ? this.onDoctorSelectionChanged : this.onAssistantSelectionChanged}
        itemLimit={1}
      />
    );
  }

  private hidePairingRulesSidePanel = (): void => {
    this.setState({ filterText: undefined });
    this.onCancelClicked();
    this.props.actions.setShowPairingRules(false);
  };

  private generateProviderConversions(props: PairingRulesSidePanelProps) {
    if (props.allProviders && props.allProviders.data && !props.allProviders.isFetching) {
      const providerIdToProvider: { [key: string]: ProviderData } = {};
      props.allProviders.data.forEach((p) => {
        providerIdToProvider[p.providerId] = p;
      });
      this.setState({ providerIdToProvider }, () => this.setPairingRules(props.pairingRules.data || []));
    }
  }

  private setPairingRules(pairingRules: NotAllowedMatch[]) {
    const pairingRulesItemsList = pairingRules.filter(
      (pr) => this.state.providerIdToProvider[pr.providerOneId] && this.state.providerIdToProvider[pr.providerOneId].providerType === 'MEDICAL_ASSISTANT'
    );

    this.setState({ pairingRulesItemsList });
  }

  private onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    this.setState({ filterText: newValue });
  };

  private onSearchChangeDoctor = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    return this.filterProvidersByTextAndType(filterText, 'DOCTOR');
  };

  private onSearchChangeMA = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    return this.filterProvidersByTextAndType(filterText, 'MEDICAL_ASSISTANT');
  };

  private filterProvidersByTextAndType(filterText: string, providerType: string): IPersonaProps[] | Promise<IPersonaProps[]> {
    filterText = filterText.toLowerCase();
    if (this.props.allProviders.isFetching) {
      return [];
    }
    return this.props.allProviders
      .data!.filter((p) => p.fullName.toLowerCase().indexOf(filterText!) > -1 && providerType === p.providerType)
      .map((p) => ({
        text: p.fullName,
        imageUrl: p.avatarUrl,
        secondaryText: p.providerId,
        showSecondaryText: false,
      }));
  }

  private onDoctorSelectionChanged = (items?: IPersonaProps[]) => {
    this.setState({
      selectedDoctorId: items && items.length > 0 ? items[0].secondaryText : undefined,
      // Force re-render of DetailsList
      pairingRulesItemsList: this.state.pairingRulesItemsList.slice(),
    });
  };

  private onAssistantSelectionChanged = (items?: IPersonaProps[]) => {
    this.setState({
      selectedAssistantId: items && items.length > 0 ? items[0].secondaryText : undefined,
      pairingRulesItemsList: this.state.pairingRulesItemsList.slice(),
    });
  };

  private onAddClicked = () => {
    const newItem: NotAllowedMatch = {
      id: '',
      providerOneId: '',
      providerTwoId: '',
      new: true,
    };

    const newArray = this.state.pairingRulesItemsList.slice();
    newArray.unshift(newItem);
    this.setState({
      pairingRulesItemsList: newArray,
      insertingNewPairingRule: true,
    });
  };

  private onCancelClicked = () => {
    const pairingRulesFiltered = this.state.pairingRulesItemsList.filter((pr) => !pr.new);
    this.setState({
      selectedDoctorId: undefined,
      selectedAssistantId: undefined,
      pairingRulesItemsList: pairingRulesFiltered,
      insertingNewPairingRule: false,
    });
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'MEDICAL_ASSISTANT',
        name: 'Medical Assistant',
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: NotAllowedMatch) => {
          if (item && item.new) {
            return this.renderNormalPicker('MEDICAL_ASSISTANT');
          } else {
            const provider = this.state.providerIdToProvider[item.providerOneId];
            if (!provider) {
              return null;
            }
            return <Persona size={PersonaSize.size24} text={provider.fullName} imageUrl={provider.avatarUrl} />;
          }
        },
      },
      {
        key: 'DOCTOR',
        name: 'Provider',
        minWidth: 100,
        maxWidth: 200,
        onRender: (item: NotAllowedMatch) => {
          if (item && item.new) {
            return this.renderNormalPicker('DOCTOR');
          } else {
            const provider = this.state.providerIdToProvider[item.providerTwoId];
            if (!provider) {
              return null;
            }
            return <Persona size={PersonaSize.size24} text={provider.fullName} imageUrl={provider.avatarUrl} />;
          }
        },
      },
      {
        key: 'controls',
        name: '',
        fieldName: 'controls',
        minWidth: 100,
        maxWidth: 200,
        isResizable: false,
        onRender: (item?: NotAllowedMatch) => {
          if (item && item.new) {
            return (
              <div>
                <IconButton disabled={!this.state.selectedAssistantId || !this.state.selectedDoctorId} onClick={this.insertPairingRules} iconProps={{ iconName: 'Accept' }} />
                <IconButton onClick={this.onCancelClicked} iconProps={{ iconName: 'Cancel' }} />
              </div>
            );
          } else {
            return (
              <div>
                <IconButton
                  onClick={() => {
                    const providerOne = this.state.providerIdToProvider[item!.providerOneId];
                    const providerTwo = this.state.providerIdToProvider[item!.providerTwoId];
                    if (!providerOne || !providerTwo) {
                      return;
                    }
                    OkCancelDialog.show({
                      title: 'Confirm removal of pairing rule',
                      subText: `Removing this pairing rule will allow the system to pair ${providerOne.fullName} and ${providerTwo.fullName}. Are you sure you want to remove this pairing rule?`,
                      onOk: () => this.removePairingRules(item!.id, item!.providerTwoId),
                    });
                  }}
                  iconProps={{ iconName: 'Trash' }}
                />
              </div>
            );
          }
        },
      },
    ];
  }

  private insertPairingRules = () => {
    if (this.state.selectedAssistantId && this.state.selectedDoctorId) {
      HealAPI.insertPairingRules(this.state.selectedAssistantId, [this.state.selectedDoctorId])
        .then((response) => {
          if (response.ok && response.data) {
            this.setState({
              selectedDoctorId: undefined,
              selectedAssistantId: undefined,
              insertingNewPairingRule: false,
            });
            this.props.actions.fetchPairingRules();
          }
        })
        .catch((e) => {
          this.props.actions!.toast({
            type: 'error',
            title: 'Error',
            content: e.error || 'An error occurred, please try again later',
          });
        });
    }
  };

  private removePairingRules = (id: string, providerId: string) => {
    HealAPI.removePairingRules(id, [providerId])
      .then((response) => {
        if (response.ok && response.data) {
          this.props.actions.fetchPairingRules();
        }
      })
      .catch((e) => {
        this.props.actions!.toast({
          type: 'error',
          title: 'Error',
          content: e.error || 'An error occurred, please try again later',
        });
      });
  };
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    showPairingRules: state.ui.showPairingRules,
    allProviders: state.providers.allProviders,
    pairingRules: state.scheduling.pairingRules,
  };
}

export function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators(
      {
        toast,
        setShowPairingRules,
        fetchPairingRules,
      },
      dispatch
    ),
  };
}

export const PairingRulesSidePanel = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(PairingRules);
